import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { alpha } from '@mui/material/styles'
import { Chip, ListItem, Menu, Skeleton, Typography } from 'flipper-ui'
import {
    IconAccountCircle,
    IconAutoAwesome,
    IconExitToApp,
    IconExpandMore,
    IconNavigateBefore
} from 'flipper-ui/icons/mui'
import { not } from 'ramda'
import { authChannel } from '@app/App'
import { useEmployee } from '@core/graphql/resolvers/employee'
import { useDesignVersion } from '@core/layout/design-version/useDesignVersion'
import { getLogoutPath, removeAuthToken } from '@core/lib/cache'
import { Flex, FlexItemsCenter } from '@core/commonStyles'
import {
    ANCHOR_ORIGIN,
    AvatarContainer,
    CustomList,
    TRANSFORM_ORIGIN,
    UserListItems
} from './styles'
import { theme as flipperTheme } from 'flipper-ui/theme'

const { gray, neutral, orange, primary } = flipperTheme.colors

const DESIGN_TOGGLE_HIGHLIGHT_STYLE = {
    backgroundColor: alpha(orange[600], 0.08)
}
const DESIGN_TOGGLE_HIGHLIGHT_ICON_STYLE = { color: orange[600] }
const DESIGN_TOGGLE_NEUTRAL_STYLE = { color: gray[600] }
const DESIGN_TOGGLE_TITLE_STYLE = {
    gap: 16,
    justifyContent: 'space-between'
}

const V2_AVATAR_STYLE = {
    backgroundColor: primary.main,
    color: neutral[50]
}

const renderTitle = (userName: string, isV2: boolean) => {
    if (!userName) {
        return <Skeleton height={12} width={128} />
    }

    return isV2 ? <Typography>{userName}</Typography> : userName
}

const renderSubtitle = (
    branchNameToShow: string | undefined | null,
    loading: boolean,
    isV2: boolean
) => {
    if (loading) {
        return <Skeleton height={12} width={96} />
    }

    if (!isV2) {
        return branchNameToShow ?? undefined
    }

    return (
        <Typography variant='caption' color='textSecondary'>
            {branchNameToShow}
        </Typography>
    )
}

const UserOptions = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const userOptions = useRef<HTMLDivElement>(null)
    const [openMenu, setOpenMenu] = useState<boolean>(false)

    const { employee, loading } = useEmployee()
    const { isV2, toggling, toggle } = useDesignVersion()

    const userName = employee?.name || ''
    const userBranch = employee?.branchName || ''
    const userPicture = employee?.profilePicture || ''

    const handleToggleUserMenu = useCallback(() => {
        setOpenMenu(not)
    }, [])

    const handleGoTo = (route: string) => () => {
        navigate(route)
        setOpenMenu(false)
    }

    const handleSignOut = () => {
        removeAuthToken()
        authChannel.postMessage({ message: 'LOGOUT' })

        navigate(getLogoutPath())
    }

    const handleToggleDesignVersion = async () => {
        await toggle()
        setOpenMenu(false)
    }

    return (
        <Flex>
            <div ref={userOptions}>
                <UserListItems
                    id='avatar-menu'
                    key='avatar-menu'
                    padding='0 12px'
                    avatar={
                        userName ? (
                            <AvatarContainer
                                name={userName}
                                src={userPicture}
                                style={isV2 ? V2_AVATAR_STYLE : undefined}
                            />
                        ) : (
                            <Skeleton circle height={36} width={36} />
                        )
                    }
                    title={renderTitle(userName, isV2)}
                    subtitle={renderSubtitle(userBranch, loading, isV2)}
                    action={
                        openMenu ? <IconNavigateBefore /> : <IconExpandMore />
                    }
                    onClick={handleToggleUserMenu}
                />
            </div>
            <Menu
                open={openMenu}
                anchorEl={userOptions.current as HTMLDivElement}
                anchorOrigin={ANCHOR_ORIGIN}
                transformOrigin={TRANSFORM_ORIGIN}
                onClose={handleToggleUserMenu}>
                <CustomList>
                    <ListItem
                        id='my-account'
                        title={t('menu:myAccount')}
                        icon={<IconAccountCircle />}
                        onClick={handleGoTo('/my-account/profile')}
                    />
                    <ListItem
                        id='design-version-toggle'
                        style={
                            isV2
                                ? DESIGN_TOGGLE_NEUTRAL_STYLE
                                : DESIGN_TOGGLE_HIGHLIGHT_STYLE
                        }
                        icon={
                            <IconAutoAwesome
                                style={
                                    isV2
                                        ? undefined
                                        : DESIGN_TOGGLE_HIGHLIGHT_ICON_STYLE
                                }
                            />
                        }
                        title={
                            isV2 ? (
                                'Voltar ao design antigo'
                            ) : (
                                <FlexItemsCenter
                                    style={DESIGN_TOGGLE_TITLE_STYLE}>
                                    <span>Experimentar novo design</span>
                                    <Chip
                                        square
                                        size='small'
                                        color='warning'
                                        label='NOVO'
                                    />
                                </FlexItemsCenter>
                            )
                        }
                        disabled={toggling}
                        onClick={handleToggleDesignVersion}
                    />
                    <ListItem
                        id='exit'
                        title={t('menu:exit')}
                        icon={<IconExitToApp />}
                        onClick={handleSignOut}
                    />
                </CustomList>
            </Menu>
        </Flex>
    )
}

export default UserOptions
